import { useQueryClient } from '@tanstack/react-query';
import type {
  UpdateAdditionalDataRequest,
  UpdateBasicDataRequest,
  UpdateContactDataRequest,
  UpdateDocumentDataRequest,
  UpdateEmployeeDataViewModel,
  UpdateFinancialDataViewModel,
  UpdatePersonRolesViewModel,
  UpdateStudentDataViewModel,
  UpdateTeacherDataViewModel,
} from '@/shared/api/generated';
import {
  getApiV1PersonIdQueryOptions,
  getApiV1PersonQueryOptions,
  usePutApiV1PersonIdAdditionalData,
  usePutApiV1PersonIdBasicData,
  usePutApiV1PersonIdContactData,
  usePutApiV1PersonIdDocuments,
  usePutApiV1PersonIdEmployeeData,
  usePutApiV1PersonIdFinancialData,
  usePutApiV1PersonIdRoles,
  usePutApiV1PersonIdStudentData,
  usePutApiV1PersonIdTeacherData,
} from '@/shared/api/generated';

import type { PersonFormValues } from '../ui/organisms/PersonForm/personFormSchema';

// ─── Tipos ────────────────────────────────────────────────────────────────────

/**
 * Estrutura plana de `dirtyFields` do react-hook-form: cada chave pode ser um
 * boolean (campos primitivos) ou um objeto aninhado (sub-entidades como
 * `student`, `employee`, `teachingStaff`).
 */
export type PersonDirtyFields = Record<string, unknown>;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function isDirty(dirty: PersonDirtyFields, key: string): boolean {
  const value = dirty[key];
  if (typeof value === 'boolean') return value;
  if (value !== null && typeof value === 'object') {
    return Object.values(value as Record<string, unknown>).some(v => v === true);
  }
  return false;
}

function someDirty(dirty: PersonDirtyFields, keys: ReadonlyArray<string>): boolean {
  return keys.some(k => isDirty(dirty, k));
}

// ─── Campos por endpoint ──────────────────────────────────────────────────────

const BASIC_DATA_KEYS = [
  'firstName',
  'lastName',
  'alias',
  'email',
  'birthDate',
  'gender',
  'martialStatus',
  'mothersName',
  'fathersName',
  'spouseName',
  'nationalityId',
  'birthCityId',
  'birthStateId',
  'birthCountryId',
  'birthStateName',
  'birthCityName',
] as const;

const DOCUMENTS_KEYS = [
  'cpf',
  'rg',
  'dispatcherOrgan',
  'dispatcherOrganState',
  'dateDispatch',
  'passportNumber',
  'idCountryNumber',
] as const;

const CONTACT_DATA_KEYS = [
  'celPhone',
  'homePhone',
  'workPhone',
  'receiveSms',
  'receiveMarketing',
] as const;

const ADDITIONAL_DATA_KEYS = [
  'urlLinkedin',
  'urlLattes',
  'miniCurriculum',
  'observation',
  'isPCD',
  'pcdDescription',
  'useAccessibilityTool',
  'accessibilityToolDescription',
  'profession',
  'professionId',
  'company',
  'associatedCompanyId',
  'jobPositionId',
  'otherJobPosition',
  'businessSectorId',
  'otherBusinessSector',
  'serviceTime',
  'experienceLevel',
  'selfEmployed',
  'isWorking',
  'academicEducation',
  'academicInstitutionId',
  'academicCourseName',
  'completionDate',
] as const;

const ROLES_KEYS = ['isStudent', 'isMonitor', 'isEmployee', 'isTeachingStaff'] as const;

const FINANCIAL_KEYS = [
  'financialBankName',
  'financialAgency',
  'financialCheckingAccount',
  'financialSavingAccount',
  'financialPaymentMethod',
  'financialAccountType',
] as const;

// Campos de additional-data que aparecem na aba "Pessoal" (acessibilidade)
const PERSONAL_ADDITIONAL_KEYS = [
  'isPCD',
  'pcdDescription',
  'useAccessibilityTool',
  'accessibilityToolDescription',
] as const;

// Campos de additional-data que aparecem na aba "Contato" (links e textos)
const CONTACT_ADDITIONAL_KEYS = [
  'urlLinkedin',
  'urlLattes',
  'miniCurriculum',
  'observation',
] as const;

const STEP_TO_KEYS: Readonly<Record<string, ReadonlyArray<string>>> = {
  personal: [...BASIC_DATA_KEYS, ...DOCUMENTS_KEYS, ...PERSONAL_ADDITIONAL_KEYS],
  contact: [...CONTACT_DATA_KEYS, ...CONTACT_ADDITIONAL_KEYS],
  professional: [...ADDITIONAL_DATA_KEYS],
  documents: [],
  addresses: [],
  profiles: [...ROLES_KEYS, 'student', 'employee', 'teachingStaff'],
  financial: [...FINANCIAL_KEYS],
};

// ─── Builders de payload ──────────────────────────────────────────────────────

/** Helper: garante string|null (descarta tipos exóticos do schema). */
function asStr(v: unknown): string | null {
  if (v == null || v === '') return null;
  return String(v);
}

function asNum(v: unknown): number | null {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
}

function asBool(v: unknown): boolean | null {
  if (typeof v === 'boolean') return v;
  return null;
}

function buildBasicData(values: PersonFormValues): UpdateBasicDataRequest {
  return {
    firstName: values.firstName,
    lastName: values.lastName,
    alias: asStr(values.alias),
    email: values.email,
    birthDate: asStr(values.birthDate),
    gender: values.gender,
    martialStatus: values.martialStatus,
    mothersName: asStr(values.mothersName),
    fathersName: asStr(values.fathersName),
    spouseName: asStr(values.spouseName),
    nationalityId: asNum(values.nationalityId),
    birthCityId: asNum(values.birthCityId),
    birthStateId: asNum(values.birthStateId),
    birthCountryId: asNum(values.birthCountryId),
    birthStateName: asStr(values.birthStateName),
    birthCityName: asStr(values.birthCityName),
  } as UpdateBasicDataRequest;
}

function buildDocuments(values: PersonFormValues): UpdateDocumentDataRequest {
  return {
    cpf: values.cpf,
    rg: asStr(values.rg),
    dispatcherOrgan: asStr(values.dispatcherOrgan),
    dispatcherOrganState: asStr(values.dispatcherOrganState),
    dateDispatch: asStr(values.dateDispatch),
    passportNumber: asStr(values.passportNumber),
    idCountryNumber: asStr(values.idCountryNumber),
  } as UpdateDocumentDataRequest;
}

function buildContactData(values: PersonFormValues): UpdateContactDataRequest {
  return {
    celPhone: values.celPhone,
    homePhone: asStr(values.homePhone),
    workPhone: asStr(values.workPhone),
    receiveSms: asBool(values.receiveSms),
    receiveMarketing: asBool(values.receiveMarketing),
  } as UpdateContactDataRequest;
}

function buildAdditionalData(values: PersonFormValues): UpdateAdditionalDataRequest {
  return {
    profession: asStr(values.profession),
    professionId: asNum(values.professionId),
    company: asStr(values.company),
    associatedCompanyId: asNum(values.associatedCompanyId),
    jobPositionId: asNum(values.jobPositionId),
    otherJobPosition: asStr(values.otherJobPosition),
    businessSectorId: asNum(values.businessSectorId),
    otherBusinessSector: asStr(values.otherBusinessSector),
    serviceTime: values.serviceTime,
    experienceLevel: values.experienceLevel,
    selfEmployed: asBool(values.selfEmployed),
    isWorking: asBool(values.isWorking),
    academicEducation: values.academicEducation,
    academicInstitutionId: asNum(values.academicInstitutionId),
    academicCourseName: asStr(values.academicCourseName),
    completionDate: asStr(values.completionDate),
    urlLinkedin: asStr(values.urlLinkedin),
    urlLattes: asStr(values.urlLattes),
    miniCurriculum: asStr(values.miniCurriculum),
    observation: asStr(values.observation),
    isPCD: asBool(values.isPCD),
    pcdDescription: asStr(values.pcdDescription),
    useAccessibilityTool: asBool(values.useAccessibilityTool),
    accessibilityToolDescription: asStr(values.accessibilityToolDescription),
  } as UpdateAdditionalDataRequest;
}

function buildRoles(values: PersonFormValues): UpdatePersonRolesViewModel {
  return {
    isStudent: asBool(values.isStudent),
    isMonitor: asBool(values.isMonitor),
    isEmployee: asBool(values.isEmployee),
    isTeacher: asBool(values.isTeachingStaff),
  } as UpdatePersonRolesViewModel;
}

function buildStudentData(values: PersonFormValues): UpdateStudentDataViewModel {
  return {
    ra: asStr(values.student?.ra),
    codeUsp: asStr(values.student?.codeUsp),
    curriculum: asStr(values.student?.curriculum),
    perfilPublic: asBool(values.student?.perfilPublic),
  } as UpdateStudentDataViewModel;
}

function buildEmployeeData(values: PersonFormValues): UpdateEmployeeDataViewModel {
  return {
    postId: asNum(values.employee?.postId),
    codeBadge: asStr(values.employee?.codeBadge),
    dateStart: asStr(values.employee?.dateStart),
    dateFinish: asStr(values.employee?.dateFinish),
    type: values.employee?.type,
    serviceRequestNotification: values.employee?.serviceRequestNotification ?? false,
  } as UpdateEmployeeDataViewModel;
}

function buildTeacherData(values: PersonFormValues): UpdateTeacherDataViewModel {
  return {
    observation: asStr(values.teachingStaff?.observation),
    isUspTeaching: asBool(values.teachingStaff?.isUspTeaching),
    codeUSP: asStr(values.teachingStaff?.codeUSP),
    certNumber: asStr(values.teachingStaff?.certNumber),
    uspContractType: values.teachingStaff?.uspContractType,
    certWorkload: asNum(values.teachingStaff?.certWorkload),
    certExpirationDate: asStr(values.teachingStaff?.certExpirationDate),
    isLattes: asBool(values.teachingStaff?.isLattes),
    lattesUpdateDate: asStr(values.teachingStaff?.lattesUpdateDate),
    academicEducation: values.teachingStaff?.academicEducation,
  } as UpdateTeacherDataViewModel;
}

function buildFinancialData(values: PersonFormValues): UpdateFinancialDataViewModel {
  return {
    bankName: asStr(values.financialBankName),
    agency: asStr(values.financialAgency),
    checkingAccount: asStr(values.financialCheckingAccount),
    savingAccount: asStr(values.financialSavingAccount),
    paymentMethod: values.financialPaymentMethod,
    accountType: values.financialAccountType,
  } as UpdateFinancialDataViewModel;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Orquestra a atualização de uma pessoa disparando apenas os endpoints PUT
 * cujos campos correspondentes estão sujos (modificados).
 *
 * Cada bloco do formulário é mapeado para um endpoint específico:
 * dados básicos, documentos, contato, dados adicionais, papéis, financeiro e
 * sub-entidades (aluno, funcionário, docente). Endereços não são tratados
 * aqui — o endpoint exige `addressPersonId` que não consta no payload de
 * leitura atual.
 *
 * @example
 * ```tsx
 * const updateByStep = useUpdatePersonByStep();
 * await updateByStep.submitChangedSections(personId, values, dirtyFields);
 * ```
 */
export function useUpdatePersonByStep() {
  const queryClient = useQueryClient();

  const basicData = usePutApiV1PersonIdBasicData();
  const documents = usePutApiV1PersonIdDocuments();
  const contactData = usePutApiV1PersonIdContactData();
  const additionalData = usePutApiV1PersonIdAdditionalData();
  const roles = usePutApiV1PersonIdRoles();
  const studentData = usePutApiV1PersonIdStudentData();
  const employeeData = usePutApiV1PersonIdEmployeeData();
  const teacherData = usePutApiV1PersonIdTeacherData();
  const financialData = usePutApiV1PersonIdFinancialData();

  const submitChangedSections = async (
    personId: number,
    values: PersonFormValues,
    dirty: PersonDirtyFields,
  ): Promise<void> => {
    const tasks: Array<Promise<unknown>> = [];

    if (someDirty(dirty, BASIC_DATA_KEYS)) {
      tasks.push(basicData.mutateAsync({ id: personId, data: buildBasicData(values) }));
    }

    if (someDirty(dirty, DOCUMENTS_KEYS)) {
      tasks.push(documents.mutateAsync({ id: personId, data: buildDocuments(values) }));
    }

    if (someDirty(dirty, CONTACT_DATA_KEYS)) {
      tasks.push(contactData.mutateAsync({ id: personId, data: buildContactData(values) }));
    }

    if (someDirty(dirty, ADDITIONAL_DATA_KEYS)) {
      tasks.push(additionalData.mutateAsync({ id: personId, data: buildAdditionalData(values) }));
    }

    if (someDirty(dirty, ROLES_KEYS)) {
      tasks.push(roles.mutateAsync({ id: personId, data: buildRoles(values) }));
    }

    if (isDirty(dirty, 'student')) {
      tasks.push(studentData.mutateAsync({ id: personId, data: buildStudentData(values) }));
    }

    if (isDirty(dirty, 'employee')) {
      tasks.push(employeeData.mutateAsync({ id: personId, data: buildEmployeeData(values) }));
    }

    if (isDirty(dirty, 'teachingStaff')) {
      tasks.push(teacherData.mutateAsync({ id: personId, data: buildTeacherData(values) }));
    }

    if (someDirty(dirty, FINANCIAL_KEYS)) {
      tasks.push(financialData.mutateAsync({ id: personId, data: buildFinancialData(values) }));
    }

    if (tasks.length === 0) return;

    await Promise.all(tasks);

    void queryClient.invalidateQueries(getApiV1PersonQueryOptions());
    void queryClient.invalidateQueries(getApiV1PersonIdQueryOptions(personId));
  };

  /**
   * Dispara apenas os endpoints correspondentes à etapa informada,
   * ignorando campos de outras etapas. Útil para salvar passo a passo
   * no modo edição ao navegar entre abas.
   *
   * @param stepKey - Chave da etapa atual (ex: `'personal'`, `'contact'`)
   * @param personId - ID da pessoa
   * @param values - Valores atuais do formulário
   * @param dirty - Mapa de campos modificados do react-hook-form
   */
  const submitStep = async (
    stepKey: string,
    personId: number,
    values: PersonFormValues,
    dirty: PersonDirtyFields,
  ): Promise<void> => {
    const allowedKeys = STEP_TO_KEYS[stepKey] ?? [];
    if (allowedKeys.length === 0) return;

    const stepDirty: PersonDirtyFields = {};
    for (const key of allowedKeys) {
      if (key in dirty) stepDirty[key] = dirty[key];
    }

    await submitChangedSections(personId, values, stepDirty);
  };

  const isPending =
    basicData.isPending ||
    documents.isPending ||
    contactData.isPending ||
    additionalData.isPending ||
    roles.isPending ||
    studentData.isPending ||
    employeeData.isPending ||
    teacherData.isPending ||
    financialData.isPending;

  return { submitChangedSections, submitStep, isPending } as const;
}
